import { useEffect, useState } from 'react';
import { Stack, Row, Text, Panel, Button, Tag, Badge, EmptyState, Money, Input } from '../ui';
import { api } from '../api/client';
import { MobileSheet } from '../ui';

type Member = {
  id: string; name: string; email: string; city?: string;
  role?: string;
  trial_ends_at?: number;
  fee_waived_at?: number; fee_waived_reason?: string;
  dd_reviewer_at?: number; dd_reviewer_credentials?: string;
  stripe_subscription_status?: string;
  suspended_at?: number;
  password_set_at?: number;
  created_at?: number;
};

function billingPill(m: Member) {
  if (m.suspended_at) return { label: 'suspended', tone: 'negative' as const };
  if (m.fee_waived_at) return { label: 'waived', tone: 'accent' as const };
  if (m.trial_ends_at && m.trial_ends_at > Date.now()) {
    const days = Math.ceil((m.trial_ends_at - Date.now()) / 86_400_000);
    return { label: `trial · ${days}d`, tone: days > 7 ? 'positive' as const : days > 2 ? 'warning' as const : 'negative' as const };
  }
  if (m.stripe_subscription_status === 'active') return { label: 'active', tone: 'positive' as const };
  if (m.stripe_subscription_status === 'past_due') return { label: 'past due', tone: 'negative' as const };
  if (m.stripe_subscription_status === 'canceled') return { label: 'canceled', tone: 'neutral' as const };
  if (m.stripe_subscription_status) return { label: m.stripe_subscription_status, tone: 'warning' as const };
  return { label: 'no plan', tone: 'neutral' as const };
}

export function Admin() {
  const [tab, setTab] = useState<'members'|'billing'|'activity'>('members');
  const [members, setMembers] = useState<Member[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFor, setActionFor] = useState<{ kind: string; member: Member } | null>(null);
  const [actionInput, setActionInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [m, s, a] = await Promise.all([api.adminMembers(), api.adminBillingSummary(), api.adminAuditLog()]);
      setMembers(m.members ?? []);
      setSummary(s);
      setEvents(a.events ?? []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const closeAction = () => { setActionFor(null); setActionInput(''); };

  const submitAction = async () => {
    if (!actionFor) return;
    setSubmitting(true);
    try {
      const { kind, member } = actionFor;
      if (kind === 'trial') await api.adminTrial(member.id, parseInt(actionInput || '30'));
      else if (kind === 'waive') {
        if (!actionInput.trim()) { setSubmitting(false); return; }
        await api.adminWaive(member.id, actionInput.trim());
      }
      else if (kind === 'unwaive') await api.adminUnwaive(member.id);
      else if (kind === 'suspend') await api.adminSuspend(member.id);
      else if (kind === 'restore') await api.adminRestore(member.id);
      else if (kind === 'reviewer-approve') {
        if (!actionInput.trim()) { setSubmitting(false); return; }
        await api.adminApproveReviewer(member.id, actionInput.trim());
      }
      else if (kind === 'reviewer-revoke') await api.adminRevokeReviewer(member.id);
      closeAction();
      await refresh();
    } catch (e: any) {
      alert('Failed: ' + (e.message ?? 'unknown'));
    }
    setSubmitting(false);
  };

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Stack gap={0.5}>
          <Text variant="display">Admin center</Text>
          <Text muted variant="small">manage members, billing, and audit history</Text>
        </Stack>
      </Row>

      <Row gap={0.5} className="overflow-x-auto no-scrollbar">
        {(['members','billing','activity'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`shrink-0 snap-start h-9 sm:h-7 px-3 sm:px-2 hairline rounded-sm text-xs transition-colors ${tab === t ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </Row>

      {loading ? <Text variant="small" muted>loading…</Text> : (
        <>
          {tab === 'members' && (
            <Panel title={`Members (${members.length})`}>
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted">
                    <tr className="hairline-b">
                      <th className="text-left py-2 font-normal">Name</th>
                      <th className="text-left font-normal">Email</th>
                      <th className="text-left font-normal">Role</th>
                      <th className="text-left font-normal">Billing</th>
                      <th className="text-left font-normal">DD reviewer</th>
                      <th className="text-right font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => {
                      const pill = billingPill(m);
                      return (
                        <tr key={m.id} className="hairline-b">
                          <td className="py-2">{m.name}</td>
                          <td className="text-muted text-xs">{m.email}</td>
                          <td><Tag>{m.role ?? 'member'}</Tag></td>
                          <td><Badge tone={pill.tone}>{pill.label}</Badge></td>
                          <td>{m.dd_reviewer_at ? <Badge tone="positive">approved</Badge> : <Text variant="label">—</Text>}</td>
                          <td className="text-right">
                            <Row gap={0.5} justify="end" wrap>
                              {!m.fee_waived_at && (!m.trial_ends_at || m.trial_ends_at < Date.now()) &&
                                <button onClick={() => setActionFor({ kind: 'trial', member: m })} className="text-xs text-muted hover:text-ink">trial</button>}
                              {!m.fee_waived_at ?
                                <button onClick={() => setActionFor({ kind: 'waive', member: m })} className="text-xs text-muted hover:text-ink">waive</button> :
                                <button onClick={() => setActionFor({ kind: 'unwaive', member: m })} className="text-xs text-muted hover:text-ink">unwaive</button>}
                              {!m.dd_reviewer_at ?
                                <button onClick={() => setActionFor({ kind: 'reviewer-approve', member: m })} className="text-xs text-muted hover:text-ink">approve DD</button> :
                                <button onClick={() => setActionFor({ kind: 'reviewer-revoke', member: m })} className="text-xs text-muted hover:text-ink">revoke DD</button>}
                              {!m.suspended_at ?
                                <button onClick={() => setActionFor({ kind: 'suspend', member: m })} className="text-xs text-muted hover:text-negative">suspend</button> :
                                <button onClick={() => setActionFor({ kind: 'restore', member: m })} className="text-xs text-muted hover:text-positive">restore</button>}
                            </Row>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile: cards */}
              <div className="md:hidden space-y-2">
                {members.map(m => {
                  const pill = billingPill(m);
                  return (
                    <div key={m.id} className="hairline rounded-sm p-3">
                      <Stack gap={1}>
                        <Row justify="between" align="center">
                          <Text variant="small" className="font-medium">{m.name}</Text>
                          <Tag>{m.role ?? 'member'}</Tag>
                        </Row>
                        <Text variant="label" className="truncate">{m.email}</Text>
                        <Row gap={1} wrap>
                          <Badge tone={pill.tone}>{pill.label}</Badge>
                          {m.dd_reviewer_at && <Badge tone="positive">DD reviewer</Badge>}
                        </Row>
                        <Row gap={0.5} wrap>
                          {!m.fee_waived_at && (!m.trial_ends_at || m.trial_ends_at < Date.now()) &&
                            <Button size="sm" variant="secondary" onClick={() => setActionFor({ kind: 'trial', member: m })}>trial</Button>}
                          {!m.fee_waived_at ?
                            <Button size="sm" variant="secondary" onClick={() => setActionFor({ kind: 'waive', member: m })}>waive</Button> :
                            <Button size="sm" variant="secondary" onClick={() => setActionFor({ kind: 'unwaive', member: m })}>unwaive</Button>}
                          {!m.dd_reviewer_at ?
                            <Button size="sm" variant="secondary" onClick={() => setActionFor({ kind: 'reviewer-approve', member: m })}>approve DD</Button> :
                            <Button size="sm" variant="secondary" onClick={() => setActionFor({ kind: 'reviewer-revoke', member: m })}>revoke DD</Button>}
                        </Row>
                      </Stack>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {tab === 'billing' && summary && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Panel title="MRR">
                <Money amount={(summary.mrrCents ?? 0) / 100} currency="USD" className="text-xl sm:text-2xl" />
                <Text variant="label">monthly recurring</Text>
              </Panel>
              <Panel title="Prosimos revenue">
                <Money amount={(summary.prosimosTotal?.total_cents ?? 0) / 100} currency="USD" className="text-xl sm:text-2xl" />
                <Text variant="label">{summary.prosimosTotal?.count ?? 0} paid submissions</Text>
              </Panel>
              <Panel title="Pending payouts">
                <Money amount={(summary.payouts?.pending_cents ?? 0) / 100} currency="USD" className="text-xl sm:text-2xl" />
                <Text variant="label">paid: ${((summary.payouts?.paid_cents ?? 0) / 100).toFixed(0)}</Text>
              </Panel>
              <Panel title="Member breakdown">
                <Stack gap={0.5}>
                  <Row justify="between"><Text variant="label">total</Text><Text variant="small">{summary.members?.total ?? 0}</Text></Row>
                  <Row justify="between"><Text variant="label">active</Text><Text variant="small">{summary.members?.active_subs ?? 0}</Text></Row>
                  <Row justify="between"><Text variant="label">in trial</Text><Text variant="small">{summary.members?.in_trial ?? 0}</Text></Row>
                  <Row justify="between"><Text variant="label">waived</Text><Text variant="small">{summary.members?.waived ?? 0}</Text></Row>
                  <Row justify="between"><Text variant="label">past due</Text><Text variant="small">{summary.members?.past_due ?? 0}</Text></Row>
                  <Row justify="between"><Text variant="label">suspended</Text><Text variant="small">{summary.members?.suspended ?? 0}</Text></Row>
                </Stack>
              </Panel>
            </div>
          )}

          {tab === 'activity' && (
            <Panel title="Audit log">
              {events.length === 0 ?
                <EmptyState title="no events yet" /> :
                <Stack gap={1}>
                  {events.map((e: any) => (
                    <div key={e.id} className="hairline-b py-2">
                      <Row justify="between">
                        <Row gap={1} align="center">
                          <Text variant="small" className="font-medium">{e.action}</Text>
                          {e.source === 'billing' && <Tag>billing</Tag>}
                          {e.source === 'workspace' && e.subject_type && <Tag>{e.subject_type}</Tag>}
                        </Row>
                        <Text variant="label">{new Date(e.created_at).toLocaleString()}</Text>
                      </Row>
                      <Row gap={2} wrap>
                        {e.actor_name && <Text variant="label">by {e.actor_name}</Text>}
                        {e.subject_id && <Text variant="label" className="break-all">→ {e.subject_id}</Text>}
                      </Row>
                      {e.data && <Text variant="label" className="break-all">{typeof e.data === 'string' ? e.data : JSON.stringify(e.data)}</Text>}
                    </div>
                  ))}
                </Stack>
              }
            </Panel>
          )}
        </>
      )}

      {actionFor && (
        <MobileSheet open={!!actionFor} onClose={closeAction} title={
          actionFor.kind === 'trial' ? `Start trial for ${actionFor.member.name}` :
          actionFor.kind === 'waive' ? `Waive fees for ${actionFor.member.name}` :
          actionFor.kind === 'unwaive' ? `Remove waiver for ${actionFor.member.name}` :
          actionFor.kind === 'suspend' ? `Suspend ${actionFor.member.name}` :
          actionFor.kind === 'restore' ? `Restore ${actionFor.member.name}` :
          actionFor.kind === 'reviewer-approve' ? `Approve ${actionFor.member.name} as DD reviewer` :
          `Revoke DD reviewer role from ${actionFor.member.name}`
        }>
          <Stack gap={2}>
            {actionFor.kind === 'trial' &&
              <Stack gap={1}>
                <Text variant="label">days (default 30)</Text>
                <Input value={actionInput} onChange={e => setActionInput(e.target.value)} placeholder="30" />
              </Stack>}
            {actionFor.kind === 'waive' &&
              <Stack gap={1}>
                <Text variant="label">reason (required, will be audited)</Text>
                <Input value={actionInput} onChange={e => setActionInput(e.target.value)} placeholder="founding member · early access" />
              </Stack>}
            {actionFor.kind === 'reviewer-approve' &&
              <Stack gap={1}>
                <Text variant="label">credentials (required, audited)</Text>
                <Input value={actionInput} onChange={e => setActionInput(e.target.value)} placeholder="12 years compliance at Citi · CAMS certified" />
              </Stack>}
            {(actionFor.kind === 'unwaive' || actionFor.kind === 'suspend' || actionFor.kind === 'restore' || actionFor.kind === 'reviewer-revoke') &&
              <Text variant="small" muted>This action takes effect immediately.</Text>}

            <Row gap={1}>
              <Button variant="primary" onClick={submitAction} disabled={submitting}>{submitting ? 'working…' : 'confirm'}</Button>
              <Button variant="secondary" onClick={closeAction}>cancel</Button>
            </Row>
          </Stack>
        </MobileSheet>
      )}
    </Stack>
  );
}
